import React from 'react'
import { ChevronRightIcon, DotsIcon } from '@mote/assets/icons'
import { preventBubblingEvent } from '@mote/helpers/browser'
import { Panel } from '@mote/ui'
import styled from '@xstyled/styled-components'
import { Control } from '../../shared/components'
import { useMotePlayer } from '../../shared/contexts/motePlayer.context'
import { useTranscriptTranslation } from './useTranscriptTranslation'

export const MoteCardRichOptionsMenu = ({
  onTranscriptLanguageUpdate,
  onEdit,
  isCreator,
  isEditable,
  isDeletable,
  isIdleState = false,
  triggerSize = 36
}) => {
  const { moteId, theme, showFullTranscript, toggleFullTranscript, onDelete } =
    useMotePlayer()
  const [currentLevel, setCurrentLevel] = React.useState(0)
  const { translations, translate } = useTranscriptTranslation(
    moteId,
    isCreator,
    onTranscriptLanguageUpdate
  )

  const ITEMS = React.useMemo(
    () =>
      [
        {
          text: showFullTranscript
            ? 'Hide full transcript'
            : 'Show full transcript',
          onClick: toggleFullTranscript,
          enabled: !isIdleState
        },
        {
          text: 'Translate to...',
          hasSubmenu: true,
          enabled: !isIdleState && translations.length > 0
        },
        {
          text: 'Edit',
          onClick: event => {
            preventBubblingEvent(event)

            onEdit?.()
          },
          enabled: isIdleState && isCreator && isEditable
        },
        {
          text: 'Delete',
          onClick: event => {
            preventBubblingEvent(event)
            onDelete()
          },
          enabled: isIdleState && isCreator && isDeletable
        }
      ].filter(item => item.enabled),
    [
      showFullTranscript,
      isIdleState,
      translations.length,
      isCreator,
      isEditable,
      isDeletable
    ]
  )

  const handleLevelChange = level => {
    setCurrentLevel(level)
  }

  const children = React.useMemo(() => {
    return currentLevel === 0
      ? ITEMS.map(item => {
          return (
            <Panel.Item
              key={item.text}
              onClick={item.onClick}
              hasSubmenu={item.hasSubmenu}
            >
              {item.text}
              {item.hasSubmenu && (
                <ChevronRightIcon
                  width='0.75em'
                  height='0.75em'
                  color='currentColor'
                  aria-hidden={true}
                  focusable={false}
                />
              )}
            </Panel.Item>
          )
        })
      : translations.map(([languageCode, name]) => {
          return (
            <Panel.Item
              key={languageCode}
              onClick={() => {
                translate(languageCode)
              }}
            >
              {name}
            </Panel.Item>
          )
        })
  }, [ITEMS, translations, currentLevel])

  if (children.length === 0) return null

  return (
    <StyledPanel
      id={`mote-card-options-${moteId}`}
      position='top-right'
      maxWidth={204}
      maxHeight={204}
      width='100vw'
      height='100vh'
      aria-label='Options'
      variant='dark'
      onLevelChange={handleLevelChange}
      triggerElement={
        <Trigger
          size={triggerSize}
          variant='rich'
          tooltipProps={{
            content: 'Options',
            distance: 7,
            direction: 'down-end'
          }}
          moteTheme={theme?.button}
        >
          <DotsIcon />
        </Trigger>
      }
    >
      {children}
    </StyledPanel>
  )
}

const StyledPanel = styled(Panel)`
  z-index: 20;
`

const Trigger = styled(Control)`
  color: ${({ moteTheme }) => moteTheme?.color};

  svg {
    height: 1.5rem;
    width: 1.5rem;
  }

  &:focus {
    box-shadow: outline;
  }

  &:hover,
  &:focus {
    color: ${({ moteTheme }) => moteTheme?.hover.color};
    background: ${({ moteTheme }) => moteTheme?.hover.background};
  }
`
